import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { Autocomplete, useAutocomplete } from '@elipssolution/harfang';
import { useTheme } from '@mui/material';
import getConfig from 'next/config';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomerFileType } from '../../../types/customerFile';
import {
	FETCH_CUSTOMER_FILES,
	FetchCustomerFilesType,
	FETCH_CUSTOMER_FILES_BY_MODULE_CODE,
	FetchCustomerFilesByModuleCodeType,
} from '../../api/customerFile';
import usePreviousRoute from '../../hooks/usePreviousRoute';
import { PublicRuntimeConfigType } from '../../types/publicRuntimeConfig';
import { useSession } from '../session/SessionContext';

const MobileCustomerFileSelector = () => {
	const { shape, shadows, spacing } = useTheme();
	const borderRadius = `${shape.borderRadius * 2}px`;

	const prevPath = usePreviousRoute();
	const { publicRuntimeConfig } = getConfig() as PublicRuntimeConfigType;
	const { standaloneModule } = publicRuntimeConfig;
	const {
		changeCustomerFile: changeSessionCustomerFile,
		customerFile: sessionCustomerFile,
		refetch: refetchSession,
	} = useSession();

	const sidebarCustomerFileAutocompleteInstance = useAutocomplete();

	// This state is volontary redundant with the customerFile of session because we need to display the selected user before the session refetch.
	const [customerFile, setCustomerFile] = useState<CustomerFileType>();

	const [fetchCustomerFiles, { networkStatus: fetchCustomerFilesNetworkStatus }] = useLazyQuery<FetchCustomerFilesType>(
		FETCH_CUSTOMER_FILES,
		{
			notifyOnNetworkStatusChange: true,
		},
	);

	const [fetchCustomerFilesForStandaloneModule, { networkStatus: fetchCustomerFilesForStandaloneModuleNetworkStatus }] =
		useLazyQuery<FetchCustomerFilesByModuleCodeType>(FETCH_CUSTOMER_FILES_BY_MODULE_CODE, {
			notifyOnNetworkStatusChange: true,
		});

	const customerFileDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			items: CustomerFileType[];
			count: number;
		}> => {
			const { data, error } = standaloneModule
				? await fetchCustomerFilesForStandaloneModule({
						variables: {
							moduleCode: standaloneModule,
							page: {
								limit,
								offset,
							},
							search,
						},
				  })
				: await fetchCustomerFiles({
						variables: {
							page: {
								limit,
								offset,
							},
							search,
						},
				  });

			if (error) {
				throw error;
			}

			if (standaloneModule) {
				const { count = 0, items = [] } = (data as FetchCustomerFilesByModuleCodeType)?.customerFilesByModuleCode ?? {};

				return { count, items };
			}

			const { count = 0, items = [] } = (data as FetchCustomerFilesType)?.customerFiles ?? {};

			return { count, items };
		},
		[fetchCustomerFiles, fetchCustomerFilesForStandaloneModule, standaloneModule],
	);

	const handleCustomerFileChange = (selectedCustomerFile?: CustomerFileType) => {
		if (selectedCustomerFile) {
			changeSessionCustomerFile(selectedCustomerFile);
			setCustomerFile(selectedCustomerFile);
		}
	};

	useEffect(() => {
		if (sessionCustomerFile) {
			setCustomerFile(sessionCustomerFile);
		}
	}, [sessionCustomerFile]);

	// Handling navigating from customerFile-not-found error to reset the selected customerFile
	useEffect(() => {
		if (prevPath?.includes('/error/404?error=customerFile-not-found')) {
			(async () => {
				await refetchSession();
			})().catch((error) => {
				throw error;
			});
		}
	}, [prevPath, refetchSession]);

	useEffect(() => {
		if (
			fetchCustomerFilesNetworkStatus === NetworkStatus.refetch ||
			fetchCustomerFilesForStandaloneModuleNetworkStatus === NetworkStatus.refetch
		) {
			sidebarCustomerFileAutocompleteInstance.reload();
		}
	}, [
		fetchCustomerFilesForStandaloneModuleNetworkStatus,
		fetchCustomerFilesNetworkStatus,
		sidebarCustomerFileAutocompleteInstance,
	]);

	const { autocomplete: mobileCustomerFileAutocompleteSx, paper: mobileCustomerFileAutocompletePaperSx } = useMemo(
		() => ({
			autocomplete: {
				zIndex: 1301,
				borderRadius,
				boxShadow: shadows[1],
				marginBottom: spacing(1),

				'& > div > div': {
					borderRadius,
					padding: `${spacing(1)}!important`,
					backgroundColor: '#CED0D6',

					'& > input': {
						paddingRight: '24px !important',
					},

					'& > fieldset': {
						border: 'none',
					},
				},
			},
			paper: {
				borderRadius,
				position: 'relative',
				bottom: '41px',
				paddingTop: '41px',
			},
		}),
		[borderRadius, shadows, spacing],
	);

	return (
		<Autocomplete<CustomerFileType>
			autocomplete={sidebarCustomerFileAutocompleteInstance}
			placeholder="Aucune entreprise"
			dataSource={customerFileDataSource}
			getOptionLabel={({ code, name }) => `${code} - ${name}`}
			onChange={handleCustomerFileChange}
			value={customerFile}
			sx={mobileCustomerFileAutocompleteSx}
			disableClearable
			disablePortal
			paperSx={mobileCustomerFileAutocompletePaperSx}
		/>
	);
};

export default MobileCustomerFileSelector;
